import { DASH, formatHours, formatMoney } from "../builder/format";
import { originalNote } from "../builder/scopeLines";
import {
  groupHours,
  groupRevenue,
  type ChildLine,
  type ChildService,
  type GroupHours,
} from "./childScope";
import "../builder/builder.css";

interface Props {
  title: string;
  services: ChildService[];
  currency: string;
}

export const hoursText = ({ original, added }: GroupHours) =>
  original === null
    ? `+${formatHours(added)}`
    : `${formatHours(original)} → ${formatHours(original + added)} (+${formatHours(added)})`;

const note = (line: ChildLine) =>
  line.source
    ? originalNote({
        quantity: line.source.quantity,
        effort: line.source.effort,
        customHours: line.customHours,
      })
    : line.sourceMissing
    ? "Original line removed"
    : null;

// The change request project's lines, read-only, in the New page's columns.
// Scope is edited in the change request project itself.
const ScopeChangesGrid = ({ title, services, currency }: Props) => {
  if (services.length === 0) return null;

  const row = (line: ChildLine, group: ChildLine[], isSub: boolean) => {
    const revenue = groupRevenue(group);
    const help = note(line);
    return (
      <tr key={line.key} className={isSub ? "changeScopeGridSub" : undefined}>
        <td>
          {line.name}
          {!line.source && !line.sourceMissing && (
            <span className="changeOrderNewTag ml-2">New</span>
          )}
          {help && <div className="changeScopeGridHelp">{help}</div>}
        </td>
        <td>{formatHours(line.quantity)}</td>
        <td>{line.customHours ? DASH : formatHours(line.effort)}</td>
        <td>{line.resourceLabel || DASH}</td>
        <td>{hoursText(groupHours(group))}</td>
        <td>{revenue === null ? DASH : formatMoney(revenue, currency, true)}</td>
      </tr>
    );
  };

  return (
    <div className="changeScopeGrid mb-3">
      <div className="changeScopeGridHelp mb-1">{title}</div>
      <table className="table table-sm">
        <thead>
          <tr>
            <th>Service</th>
            <th>Quantity</th>
            <th>Effort (hrs each)</th>
            <th>Resource for addl effort</th>
            <th>Hours</th>
            <th>Change $</th>
          </tr>
        </thead>
        <tbody>
          {services.flatMap((s) => [
            row(s.line, [s.line, ...s.subservices], false),
            ...s.subservices.map((sub) => row(sub, [sub], true)),
          ])}
        </tbody>
      </table>
    </div>
  );
};

export default ScopeChangesGrid;
